// shell/components/data-source/data-source.ts

import { IDisposable, IndexRange, RowState } from '../types';
import { DisposableStore } from '../disposable';
import { DataSourceConfig, DataSourceEvent } from './types';

/**
 * DataSource abstracts data fetching, pagination, and caching.
 *
 * Key features:
 * - Fetches data in pages with configurable page size
 * - Provides O(1) key-to-index lookups
 * - Implements LRU cache eviction with viewport protection
 * - Supports request cancellation via AbortController
 * - Retries failed requests with exponential backoff and jitter
 *
 * Design constraints:
 * - Pure data layer - no DOM, no UI
 * - Index shifting on mutations is O(n) - suitable for low-frequency updates
 * - For high-frequency updates, use LiveDataSource with batching
 */
export class DataSource<T> implements IDisposable {
    protected readonly config: DataSourceConfig<T>;
    protected readonly keyFn: (item: T) => string;
    protected readonly pageSize: number;
    protected readonly disposables = new DisposableStore();

    // Data storage
    protected itemsByIndex = new Map<number, T>();
    protected itemsByKey = new Map<string, T>();
    protected keyToIndex = new Map<string, number>();
    protected itemStates = new Map<string, RowState>();

    // LRU tracking
    protected accessCounter = 0;
    protected accessTimestamps = new Map<number, number>();
    protected readonly maxCachedItems: number;

    // Loading state
    protected totalCount = 0;
    protected loadedRanges: IndexRange[] = [];
    private pendingPages = new Map<number, AbortController>();
    private pendingRequests = new Map<number, Promise<void>>();

    // Configuration
    protected currentSort: { column: string; direction: 'asc' | 'desc' } | null = null;
    protected currentFilters: Record<string, unknown> = {};

    // State flags
    private _isLoading = false;
    private error: Error | null = null;
    private retryCount = new Map<number, number>();

    // Viewport tracking for eviction
    private currentViewportStart: number | undefined;
    private currentViewportEnd: number | undefined;

    // Event listeners
    private listeners = new Map<DataSourceEvent, Set<Function>>();

    constructor(config: DataSourceConfig<T>) {
        this.config = config;
        this.keyFn = config.keyFn;
        this.pageSize = config.pageSize ?? 64;
        this.maxCachedItems = config.maxCachedItems ?? 10000;
    }

    // ===== Data Access =====

    /**
     * Get an item by index.
     */
    public getItem(index: number): T | undefined {
        const item = this.itemsByIndex.get(index);
        if (item) {
            this.recordAccess(index);
        }
        return item;
    }

    /**
     * Get an item by key.
     */
    public getItemByKey(key: string): T | undefined {
        const item = this.itemsByKey.get(key);
        if (item) {
            const index = this.keyToIndex.get(key);
            if (index !== undefined) {
                this.recordAccess(index);
            }
        }
        return item;
    }

    /**
     * Get items in a range.
     */
    public getItems(startIndex: number, endIndex: number): (T | undefined)[] {
        const items: (T | undefined)[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
            items.push(this.getItem(i));
        }
        return items;
    }

    /**
     * Get the index of an item by key.
     */
    public getIndexByKey(key: string): number {
        return this.keyToIndex.get(key) ?? -1;
    }

    /**
     * Get the total count of items.
     */
    public getTotalCount(): number {
        return this.totalCount;
    }

    /**
     * Get the loaded range (first to last loaded index).
     */
    public getLoadedRange(): IndexRange | null {
        if (this.loadedRanges.length === 0) return null;
        return {
            start: this.loadedRanges[0].start,
            end: this.loadedRanges[this.loadedRanges.length - 1].end
        };
    }

    /** Get the key function used by this DataSource */
    public getKeyFn(): (item: T) => string {
        return this.keyFn;
    }

    /** Get all currently loaded items, sorted by index */
    public getAllLoadedItems(): T[] {
        const items: T[] = [];
        const indices = [...this.itemsByIndex.keys()].sort((a, b) => a - b);
        for (const idx of indices) {
            const item = this.itemsByIndex.get(idx);
            if (item) items.push(item);
        }
        return items;
    }


    // ===== Loading State =====

    /**
     * Check if data is currently being loaded.
     */
    public isLoading(): boolean {
        return this._isLoading;
    }

    /**
     * Get the current error, if any.
     */
    public getError(): Error | null {
        return this.error;
    }

    /**
     * Ensure items in the given range are loaded.
     */
    public async ensureLoaded(startIndex: number, endIndex: number): Promise<void> {
        const neededPages = this.getNeededPages(startIndex, endIndex);

        if (neededPages.length === 0) return;

        const fetchPromises = neededPages.map(page => this.fetchPage(page));
        await Promise.allSettled(fetchPromises);
    }

    /**
     * Get pages that need to be loaded for a range.
     * Exposed for integration with VirtualScroller.
     */
    public getNeededPagesForRange(startIndex: number, endIndex: number): number[] {
        return this.getNeededPages(startIndex, endIndex);
    }

    // ===== Sort/Filter =====

    /**
     * Set sort column and direction.
     */
    public async setSort(column: string, direction: 'asc' | 'desc'): Promise<void> {
        this.cancelAllPendingRequests();
        this.currentSort = { column, direction };
        await this.refresh();
    }

    /**
     * Get current sort configuration.
     */
    public getSort(): { column: string; direction: 'asc' | 'desc' } | null {
        return this.currentSort;
    }

    /**
     * Set filters.
     */
    public async setFilters(filters: Record<string, unknown>): Promise<void> {
        this.cancelAllPendingRequests();
        this.currentFilters = filters;
        await this.refresh();
    }

    /**
     * Get current filters.
     */
    public getFilters(): Record<string, unknown> {
        return { ...this.currentFilters };
    }

    // ===== Mutations =====

    /**
     * Add an item.
     * Note: O(n) due to index shifting.
     */
    public addItem(
        item: T,
        position: 'start' | 'end' | { afterKey: string } = 'end'
    ): void {
        let key: string;
        try {
            key = this.keyFn(item);
        } catch (e) {
            const error = e instanceof Error ? e : new Error(String(e));
            this.config.onKeyError?.(error, item, 'addItem');
            return;
        }

        let insertIndex: number;

        if (position === 'start') {
            insertIndex = 0;
        } else if (position === 'end') {
            insertIndex = this.totalCount;
        } else {
            const afterIndex = this.keyToIndex.get(position.afterKey);
            insertIndex = afterIndex !== undefined ? afterIndex + 1 : this.totalCount;
        }

        // Shift indices - O(n)
        this.shiftIndices(insertIndex, 1);

        // Insert new item
        this.itemsByIndex.set(insertIndex, item);
        this.itemsByKey.set(key, item);
        this.keyToIndex.set(key, insertIndex);
        this.accessTimestamps.set(insertIndex, ++this.accessCounter);
        this.totalCount++;

        this.shiftLoadedRanges(insertIndex, 1);
        this.emit('change');
    }

    /**
     * Update an item.
     */
    public updateItem(key: string, item: T): void {
        const index = this.keyToIndex.get(key);
        if (index === undefined) return;

        this.itemsByIndex.set(index, item);
        this.itemsByKey.set(key, item);
        this.recordAccess(index);

        this.emit('change');
    }

    /**
     * Remove an item.
     * Note: O(n) due to index shifting.
     */
    public removeItem(key: string): void {
        const index = this.keyToIndex.get(key);
        if (index === undefined) return;

        this.itemsByIndex.delete(index);
        this.itemsByKey.delete(key);
        this.keyToIndex.delete(key);
        this.itemStates.delete(key);
        this.accessTimestamps.delete(index);

        // Shift indices - O(n)
        this.shiftIndices(index + 1, -1);
        this.totalCount--;

        this.shiftLoadedRanges(index, -1);
        this.emit('change');
    }

    // ===== Item State =====

    /**
     * Set state for an item.
     */
    public setItemState(key: string, state: Partial<RowState>): void {
        const existing = this.itemStates.get(key) ?? {};
        this.itemStates.set(key, { ...existing, ...state });
    }

    /**
     * Get state for an item.
     */
    public getItemState(key: string): RowState | undefined {
        return this.itemStates.get(key);
    }

    /**
     * Clear state for an item.
     */
    public clearItemState(key: string): void {
        this.itemStates.delete(key);
    }

    // ===== Viewport =====

    /**
     * Update viewport bounds for eviction protection.
     * Call this when visible range changes.
     */
    public setViewport(startIndex: number, endIndex: number): void {
        this.currentViewportStart = startIndex;
        this.currentViewportEnd = endIndex;
    }

    // ===== Lifecycle =====

    /**
     * Refresh all data.
     */
    public async refresh(preserveScroll = false): Promise<void> {
        this.cancelAllPendingRequests();

        if (preserveScroll) {
            this.emit('beforeRefresh');
        }

        // Clear all data
        this.itemsByIndex.clear();
        this.itemsByKey.clear();
        this.keyToIndex.clear();
        this.accessTimestamps.clear();
        // Keep itemStates - keyed by item key, not index
        this.loadedRanges = [];
        this.retryCount.clear();
        this.totalCount = 0;
        this.error = null;

        this.emit('change');

        // Fetch first page
        await this.fetchPage(0);

        if (preserveScroll) {
            this.emit('afterRefresh');
        }
    }

    /**
     * Clean up resources.
     */
    public dispose(): void {
        this.cancelAllPendingRequests();
        this.itemsByIndex.clear();
        this.itemsByKey.clear();
        this.keyToIndex.clear();
        this.itemStates.clear();
        this.accessTimestamps.clear();
        this.listeners.clear();
        this.disposables.dispose();
    }

    // ===== Events =====

    /**
     * Subscribe to an event.
     */
    public on(event: DataSourceEvent, handler: Function): () => void {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event)!.add(handler);

        return () => {
            this.listeners.get(event)?.delete(handler);
        };
    }

    // ===== Protected Methods =====

    protected emit(event: DataSourceEvent, ...args: unknown[]): void {
        const handlers = this.listeners.get(event);
        if (handlers) {
            for (const handler of handlers) {
                try {
                    handler(...args);
                } catch (e) {
                    console.error(`Error in DataSource ${event} handler:`, e);
                }
            }
        }
    }

    protected shiftIndices(fromIndex: number, delta: number): void {
        const newItemsByIndex = new Map<number, T>();
        const newKeyToIndex = new Map<string, number>();
        const newAccessTimestamps = new Map<number, number>();

        for (const [index, item] of this.itemsByIndex) {
            let key: string;
            try {
                key = this.keyFn(item);
            } catch {
                continue;
            }

            const newIndex = index >= fromIndex ? index + delta : index;

            if (newIndex >= 0) {
                newItemsByIndex.set(newIndex, item);
                newKeyToIndex.set(key, newIndex);

                const timestamp = this.accessTimestamps.get(index);
                if (timestamp !== undefined) {
                    newAccessTimestamps.set(newIndex, timestamp);
                }
            }
        }

        this.itemsByIndex = newItemsByIndex;
        this.keyToIndex = newKeyToIndex;
        this.accessTimestamps = newAccessTimestamps;
    }

    protected shiftLoadedRanges(fromIndex: number, delta: number): void {
        this.loadedRanges = this.loadedRanges
            .map(range => {
                const newStart = range.start >= fromIndex ? range.start + delta : range.start;
                const newEnd = range.end >= fromIndex ? range.end + delta : range.end;
                return { start: Math.max(0, newStart), end: Math.max(0, newEnd) };
            })
            .filter(range => range.start <= range.end);
    }

    // ===== Private Methods =====

    private getNeededPages(startIndex: number, endIndex: number): number[] {
        const needed: number[] = [];

        const startPage = Math.floor(startIndex / this.pageSize);
        const endPage = Math.floor(endIndex / this.pageSize);

        for (let page = startPage; page <= endPage; page++) {
            if (!this.pendingPages.has(page) && !this.isPageLoaded(page)) {
                needed.push(page);
            }
        }

        return needed;
    }

    private isPageLoaded(page: number): boolean {
        const pageStart = page * this.pageSize;
        const pageEnd = pageStart + this.pageSize - 1;
        const actualEnd = Math.min(pageEnd, this.totalCount - 1);

        // If totalCount is 0 or page is beyond data, not loaded
        if (this.totalCount === 0) return false;
        if (pageStart >= this.totalCount) return true; // Beyond data is "loaded"

        return this.loadedRanges.some(range =>
            range.start <= pageStart && range.end >= actualEnd
        );
    }

    private async fetchPage(page: number): Promise<void> {
        // Check if already fetching this page
        const existingPromise = this.pendingRequests.get(page);
        if (existingPromise) return existingPromise;

        // Create abort controller for this page
        const pageAbortController = new AbortController();
        this.pendingPages.set(page, pageAbortController);

        const offset = page * this.pageSize;
        const promise = this.fetchPageWithRetry(page, offset, pageAbortController.signal);
        this.pendingRequests.set(page, promise);

        try {
            await promise;
        } finally {
            this.pendingRequests.delete(page);
            this.pendingPages.delete(page);
        }
    }

    private async fetchPageWithRetry(
        page: number,
        offset: number,
        signal: AbortSignal
    ): Promise<void> {
        const maxRetries = this.config.maxRetries ?? 3;
        const baseDelay = this.config.retryDelayMs ?? 1000;

        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (signal.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }

                this.setLoading(true);

                const result = await this.config.fetch({
                    offset,
                    limit: this.pageSize,
                    sortBy: this.currentSort?.column,
                    sortDirection: this.currentSort?.direction,
                    filters: this.currentFilters,
                    signal
                });

                if (signal.aborted) {
                    throw new DOMException('Aborted', 'AbortError');
                }

                // Store items
                this.storeItems(result.items, offset);

                // Update total count
                if (result.totalCount !== this.totalCount) {
                    this.totalCount = result.totalCount;
                }

                // Track loaded range
                if (result.items.length > 0) {
                    this.addLoadedRange(offset, offset + result.items.length - 1);
                }

                // Clear error on success
                this.error = null;
                this.retryCount.delete(page);

                this.emit('change');
                return;

            } catch (error) {
                lastError = error as Error;

                // Don't retry aborted requests
                if ((error as Error).name === 'AbortError') {
                    throw error;
                }

                // Exponential backoff with jitter
                if (attempt < maxRetries) {
                    const delay = baseDelay * Math.pow(2, attempt);
                    const jitter = Math.random() * 0.3 * delay;
                    await this.sleep(delay + jitter);

                    if (signal.aborted) {
                        throw new DOMException('Aborted', 'AbortError');
                    }
                }
            } finally {
                this.setLoading(false);
            }
        }

        // All retries failed
        this.error = lastError;
        this.retryCount.set(page, maxRetries);
        this.emit('error', lastError);

        // Provide retry hook for UI
        this.config.onLoadError?.(lastError!, page, () => this.fetchPage(page));

        throw lastError;
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private storeItems(items: T[], startOffset: number): void {
        const errors: Array<{ item: T; error: Error }> = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const index = startOffset + i;

            let key: string;
            try {
                key = this.keyFn(item);
            } catch (e) {
                const error = e instanceof Error ? e : new Error(String(e));
                errors.push({ item, error });
                this.config.onKeyError?.(error, item, 'storeItems');
                continue;
            }

            this.itemsByIndex.set(index, item);
            this.itemsByKey.set(key, item);
            this.keyToIndex.set(key, index);
            this.accessTimestamps.set(index, ++this.accessCounter);
        }

        // Log summary if errors occurred
        if (errors.length > 0) {
            console.error(
                `DataSource: ${errors.length} items skipped due to keyFn errors`
            );
        }

        this.evictIfNeeded();
    }

    private recordAccess(index: number): void {
        this.accessTimestamps.set(index, ++this.accessCounter);
    }

    private evictIfNeeded(): void {
        if (this.itemsByIndex.size <= this.maxCachedItems) return;

        // Collect eviction candidates (not in viewport)
        const candidates: Array<{ index: number; timestamp: number }> = [];

        for (const [index, timestamp] of this.accessTimestamps) {
            if (!this.isInCurrentViewport(index)) {
                candidates.push({ index, timestamp });
            }
        }

        // Sort by timestamp (oldest first)
        candidates.sort((a, b) => a.timestamp - b.timestamp);

        // Evict oldest until under limit
        const toEvict = this.itemsByIndex.size - this.maxCachedItems;
        const evictedIndices: number[] = [];

        for (let i = 0; i < toEvict && i < candidates.length; i++) {
            const indexToEvict = candidates[i].index;
            const item = this.itemsByIndex.get(indexToEvict);

            if (item) {
                let key: string;
                try {
                    key = this.keyFn(item);
                } catch {
                    continue;
                }

                this.itemsByIndex.delete(indexToEvict);
                this.itemsByKey.delete(key);
                this.keyToIndex.delete(key);
                this.accessTimestamps.delete(indexToEvict);
                evictedIndices.push(indexToEvict);
                // Keep itemStates - useful if item is reloaded
            }
        }

        // Batch update loaded ranges
        if (evictedIndices.length > 0) {
            this.removeIndicesFromLoadedRanges(evictedIndices);
        }
    }

    private isInCurrentViewport(index: number): boolean {
        // Safe default: don't evict if viewport not set
        if (this.currentViewportStart === undefined ||
            this.currentViewportEnd === undefined) {
            return true;
        }
        return index >= this.currentViewportStart && index <= this.currentViewportEnd;
    }

    private addLoadedRange(start: number, end: number): void {
        if (end < start) return;

        // Merge with existing ranges
        const newRanges: IndexRange[] = [];
        let merged: IndexRange = { start, end };
        let inserted = false;

        for (const range of this.loadedRanges) {
            if (merged.start <= range.end + 1 && merged.end >= range.start - 1) {
                // Overlapping or adjacent - merge
                merged = {
                    start: Math.min(merged.start, range.start),
                    end: Math.max(merged.end, range.end)
                };
            } else if (range.end < merged.start) {
                // Range is before merged
                newRanges.push(range);
            } else {
                // Range is after merged
                if (!inserted) {
                    newRanges.push(merged);
                    inserted = true;
                }
                newRanges.push(range);
            }
        }

        if (!inserted) {
            newRanges.push(merged);
        }

        this.loadedRanges = newRanges;
    }

    private removeIndicesFromLoadedRanges(indices: number[]): void {
        if (indices.length === 0) return;

        // Sort indices for efficient processing
        const sorted = [...indices].sort((a, b) => a - b);

        // Group into contiguous ranges
        const gaps: IndexRange[] = [];
        let gapStart = sorted[0];
        let gapEnd = sorted[0];

        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i] === gapEnd + 1) {
                gapEnd = sorted[i];
            } else {
                gaps.push({ start: gapStart, end: gapEnd });
                gapStart = sorted[i];
                gapEnd = sorted[i];
            }
        }
        gaps.push({ start: gapStart, end: gapEnd });

        // Remove gaps from loaded ranges
        for (const gap of gaps) {
            this.loadedRanges = this.subtractRangeFromRanges(this.loadedRanges, gap);
        }

        // Periodic defragmentation: merge ranges that became adjacent
        this.defragmentLoadedRanges();
    }

    private subtractRangeFromRanges(
        ranges: IndexRange[],
        toRemove: IndexRange
    ): IndexRange[] {
        const result: IndexRange[] = [];

        for (const range of ranges) {
            if (range.end < toRemove.start || range.start > toRemove.end) {
                // No overlap
                result.push(range);
            } else {
                // Overlapping - may split
                if (range.start < toRemove.start) {
                    result.push({ start: range.start, end: toRemove.start - 1 });
                }
                if (range.end > toRemove.end) {
                    result.push({ start: toRemove.end + 1, end: range.end });
                }
            }
        }

        return result;
    }

    private defragmentLoadedRanges(): void {
        if (this.loadedRanges.length < 2) return;

        const merged: IndexRange[] = [];
        let current = this.loadedRanges[0];

        for (let i = 1; i < this.loadedRanges.length; i++) {
            const next = this.loadedRanges[i];
            if (current.end + 1 >= next.start) {
                // Merge
                current = { start: current.start, end: Math.max(current.end, next.end) };
            } else {
                merged.push(current);
                current = next;
            }
        }
        merged.push(current);

        this.loadedRanges = merged;
    }

    private cancelAllPendingRequests(): void {
        for (const [, controller] of this.pendingPages) {
            controller.abort();
        }
        this.pendingPages.clear();
        this.pendingRequests.clear();
    }

    private setLoading(loading: boolean): void {
        if (this._isLoading !== loading) {
            this._isLoading = loading;
            this.emit('loading', loading);
        }
    }
}
